import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ChevronLeft, Save, Upload, X, Plus, Calendar,
  Globe, Eye, EyeOff, Sparkles, Check, AlertCircle, ArrowLeft,
  Image as ImageIcon, Folder, User, Tag
} from 'lucide-react';
import { clearBlogCache } from '../../lib/publicBlogApi';
import { adminApi } from '../../lib/adminApi';
import { getBlogImageUrl } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import BlogRichEditor from './BlogRichEditor';
import AdminBlogUploads from './AdminBlogUploads';
import './AdminBlog.css';

function localDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export default function AdminBlogEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const fileInputRef = useRef(null);

  // Form State
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [visibility, setVisibility] = useState('visible');
  const [publishedAt, setPublishedAt] = useState('');

  // Organization
  const [authorId, setAuthorId] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  // SEO
  const [pageTitle, setPageTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [isSlugManual, setIsSlugManual] = useState(false);

  // Metadata Lists
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [popularTags, setPopularTags] = useState([]);

  // Modals for Quick Add
  const [showAddAuthorModal, setShowAddAuthorModal] = useState(false);
  const [newAuthorName, setNewAuthorName] = useState('');
  const [newAuthorTitle, setNewAuthorTitle] = useState('');
  const [creatingAuthor, setCreatingAuthor] = useState(false);

  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  const [showMediaModal, setShowMediaModal] = useState(false);

  // UI status
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Load lists on mount
  useEffect(() => {
    adminApi.get('/admin/blog-authors').then(res => {
      setAuthors(res.data || []);
      if (!isEditing && res.data && res.data.length > 0) {
        setAuthorId(res.data[0].id);
        setAuthorName(res.data[0].name);
      }
    }).catch(() => {});

    adminApi.get('/admin/blog-categories').then(res => {
      setCategories(res.data || []);
      if (!isEditing && res.data && res.data.length > 0) {
        setCategoryId(res.data[0].id);
        setCategoryName(res.data[0].name);
      }
    }).catch(() => {});

    adminApi.get('/admin/blog-tags').then(res => {
      setPopularTags(res.data || []);
    }).catch(() => {});
  }, [isEditing]);

  // Load post data if editing
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    adminApi.get(`/admin/blogs/${id}`)
      .then(res => {
        const p = res.data;
        setTitle(p.title || '');
        setContent(p.content || '');
        setExcerpt(p.excerpt || '');
        setFeaturedImage(getBlogImageUrl(p.featured_image || ''));
        setVisibility(p.visibility || 'visible');
        setPublishedAt(p.published_at || '');
        setAuthorId(p.author_id || '');
        setAuthorName(p.author_name || '');
        setCategoryId(p.category_id || '');
        setCategoryName(p.category_name || '');
        setTags(p.tags || []);
        setPageTitle(p.page_title || '');
        setMetaDescription(p.meta_description || '');
        setSlug(p.slug || '');
        setIsSlugManual(true);
      })
      .catch(err => {
        setError(err.response?.data?.detail || "Could not load blog post.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  // Auto-generate slug from title if not manually changed
  const handleTitleChange = (e) => {
    const val = e.target.value;
    setTitle(val);
    if (!isSlugManual) {
      const generated = val.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-');
      setSlug(generated);
    }
  };

  // Tag chip handlers
  const handleAddTag = (tagToAdd) => {
    const clean = (tagToAdd || tagInput).trim();
    if (clean && !tags.includes(clean)) {
      setTags([...tags, clean]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Image Upload handler with automatic base64 fallback (supports up to 50MB)
  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError("Image exceeds maximum size of 50MB.");
      return;
    }
    setUploadingImage(true);
    setError('');

    // Strategy 1: Multipart upload (let Axios manage boundary automatically)
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await adminApi.post('/admin/blog-upload', formData);
      if (res.data?.url) {
        setFeaturedImage(getBlogImageUrl(res.data.url));
        setUploadingImage(false);
        return;
      }
    } catch (err1) {
      console.warn("Multipart upload failed, trying base64 fallback...", err1);
      // Strategy 2: Base64 JSON upload fallback
      try {
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const res2 = await adminApi.post('/admin/blog-upload-base64', {
          filename: file.name,
          data: base64Data
        });

        if (res2.data?.url) {
          setFeaturedImage(getBlogImageUrl(res2.data.url));
          setUploadingImage(false);
          return;
        }
      } catch (err2) {
        const msg = err2.response?.data?.detail || err1.response?.data?.detail || "Failed to upload image. Please try again or choose from Uploads.";
        setError(msg);
      }
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Quick Add Author
  const handleCreateAuthor = async () => {
    if (!newAuthorName.trim()) return;
    setCreatingAuthor(true);
    try {
      const res = await adminApi.post('/admin/blog-authors', {
        name: newAuthorName.trim(),
        title: newAuthorTitle.trim()
      });
      setAuthors(prev => [...prev, res.data]);
      setAuthorId(res.data.id);
      setAuthorName(res.data.name);
      setShowAddAuthorModal(false);
      setNewAuthorName('');
      setNewAuthorTitle('');
    } catch (err) {
      alert("Failed to create author.");
    } finally {
      setCreatingAuthor(false);
    }
  };

  // Quick Add Category
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    setCreatingCategory(true);
    try {
      const res = await adminApi.post('/admin/blog-categories', {
        name: newCategoryName.trim()
      });
      setCategories(prev => [...prev, res.data]);
      setCategoryId(res.data.id);
      setCategoryName(res.data.name);
      setShowAddCategoryModal(false);
      setNewCategoryName('');
    } catch (err) {
      alert("Failed to create category.");
    } finally {
      setCreatingCategory(false);
    }
  };

  // Save / Publish
  const handleSave = async (e, targetVisibility = 'visible') => {
    if (e) e.preventDefault();
    if (!title.trim()) {
      setError("Please provide a title for the blog post.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMsg('');

    const payload = {
      title: title.trim(),
      slug: slug.trim(),
      content: content,
      excerpt: excerpt.trim(),
      featured_image: featuredImage,
      visibility: targetVisibility,
      published_at: publishedAt,
      author_id: authorId || null,
      author_name: authorName || "AI Search Advisers",
      category_id: categoryId || null,
      category_name: categoryName || "General",
      tags,
      page_title: pageTitle.trim() || title.trim(),
      meta_description: metaDescription.trim() || (excerpt.trim().slice(0, 160) || "")
    };

    try {
      if (isEditing) {
        const res = await adminApi.put(`/admin/blogs/${id}`, payload);
        setVisibility(res.data.visibility || targetVisibility);
        setPublishedAt(res.data.published_at || '');
        setSuccessMsg(targetVisibility === 'hidden' ? 'Draft saved successfully!' : 'Blog post published and saved successfully!');
      } else {
        const res = await adminApi.post('/admin/blogs', payload);
        setSuccessMsg("Blog post created successfully!");
        navigate(`/admin/blogs/edit/${res.data.id}`, { replace: true });
      }
      clearBlogCache();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save blog post. Please check details and retry.");
    } finally {
      setSaving(false);
    }
  };

  const selectedCategoryDoc = categories.find(c => c.id === categoryId);
  const categorySlugPath = selectedCategoryDoc?.slug || 'news';
  const effectivePageTitle = pageTitle || title || "Blog Post Title";
  const effectiveMetaDesc = metaDescription || excerpt || "Add a title and description to see how this blog post might appear in a search engine listing.";
  const previewUrl = `https://aisearchadvisers.com/blogs/${categorySlugPath}/${slug || 'post-handle'}`;

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-12 text-center text-slate-500 shadow-sm border">
        <div className="admin-spinner mb-3" />
        <p className="text-sm">Loading post data...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-6xl mx-auto pb-16">
      {/* Top Header & Breadcrumbs matching Shopify Images 2 & 3 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/blogs"
            className="w-9 h-9 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors"
            title="Back to Blog posts"
          >
            <ChevronLeft size={20} />
          </Link>
          <div>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Link to="/admin/blogs" className="hover:underline text-slate-500">Blog posts</Link>
              <span>/</span>
              <span className="text-slate-800 font-semibold">{isEditing ? 'Edit blog post' : 'Add blog post'}</span>
            </div>
            <h2 className="admin-title text-2xl font-bold text-slate-900 mt-0.5">
              {isEditing ? (title || 'Edit blog post') : 'Add blog post'}
            </h2>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/blogs')}
            className="text-xs font-semibold px-4"
          >
            Discard
          </Button>
          {visibility === 'hidden' && (
            <Button type="button" variant="outline" disabled={saving} onClick={e => handleSave(e, 'hidden')}>
              Save draft
            </Button>
          )}
          <Button
            type="submit"
            disabled={saving}
            className="admin-primary text-xs font-semibold px-5 flex items-center gap-2 shadow"
          >
            <Save size={15} />
            <span>{saving ? 'Saving...' : (visibility === 'hidden' || !isEditing ? 'Publish' : 'Save changes')}</span>
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div role="alert" className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}
      {successMsg && (
        <div role="status" className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg flex items-center gap-2 text-sm">
          <Check size={16} />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Two-Column Shopify Grid (Images 2, 3, 4) */}
      <div className="blog-editor-grid">
        {/* ── LEFT COLUMN (Main Content) ── */}
        <div className="space-y-6">
          {/* 1. Title Card */}
          <div className="blog-card space-y-2">
            <label className="blog-card-title block">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={title}
              onChange={handleTitleChange}
              placeholder="e.g., Blog about your latest products or deals"
              className="w-full text-base font-semibold border border-slate-300 rounded-lg px-3.5 py-2.5 focus:border-amber-500 focus:outline-none"
            />
          </div>

          {/* 2. Content Card (Rich Text Editor) */}
          <div className="blog-card space-y-2">
            <div className="flex items-center justify-between">
              <label className="blog-card-title block">Content</label>
              <span className="text-xs text-slate-400">WYSIWYG & HTML mode</span>
            </div>
            <BlogRichEditor
              value={content}
              onChange={setContent}
              placeholder="Write or paste your article content here..."
            />
          </div>

          {/* 3. Excerpt Card (Images 2, 3, 4) */}
          <div className="blog-card space-y-2">
            <div className="flex items-center justify-between">
              <label className="blog-card-title block">Excerpt</label>
              <span className="text-xs text-slate-400">Optional summary</span>
            </div>
            <p className="blog-card-subtitle text-xs text-slate-500">
              Add a summary of the post to appear below the title on your single blog post, home page, or blog listing.
            </p>
            <textarea
              rows={3}
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="A brief summary for previews and social sharing cards..."
              className="w-full text-sm border border-slate-300 rounded-lg p-3 focus:border-amber-500 focus:outline-none"
            />
          </div>

          {/* 4. Search Engine Listing Card (SEO) (Images 2 & 3) */}
          <div className="blog-card space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <h4 className="blog-card-title text-base">Search engine listing</h4>
                <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded font-medium border border-amber-200">
                  Google SERP Preview
                </span>
              </div>
              <p className="blog-card-subtitle text-xs text-slate-500">
                Add a title and description to see how this blog post might appear in a search engine listing
              </p>
            </div>

            {/* Google SERP Snippet Preview Box */}
            <div className="blog-serp-preview border border-slate-200 rounded-xl p-4 bg-white shadow-sm space-y-1">
              <div className="blog-serp-url text-xs text-slate-600 flex items-center gap-1.5">
                <Globe size={13} className="text-slate-400" />
                <span>{previewUrl}</span>
              </div>
              <h5 className="blog-serp-title text-lg font-medium text-blue-700 hover:underline">
                {effectivePageTitle}
              </h5>
              <p className="blog-serp-desc text-xs text-slate-600 line-clamp-2 leading-relaxed">
                {effectiveMetaDesc}
              </p>
            </div>

            {/* Page title field */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <label className="font-semibold text-slate-700">Page title</label>
                <span className={`text-[11px] font-mono ${pageTitle.length > 70 ? 'text-red-500 font-bold' : 'text-slate-400'}`}>
                  {pageTitle.length} of 70 characters used
                </span>
              </div>
              <input
                type="text"
                maxLength={90}
                value={pageTitle}
                onChange={(e) => setPageTitle(e.target.value)}
                placeholder={title || "SEO Page Title"}
                className="w-full text-sm border border-slate-300 rounded-lg p-2.5 focus:border-amber-500 focus:outline-none"
              />
            </div>

            {/* Meta description field */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <label className="font-semibold text-slate-700">Meta description</label>
                <span className={`text-[11px] font-mono ${metaDescription.length > 160 ? 'text-red-500 font-bold' : 'text-slate-400'}`}>
                  {metaDescription.length} of 160 characters used
                </span>
              </div>
              <textarea
                rows={3}
                maxLength={200}
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                placeholder={excerpt || "Search description snippet..."}
                className="w-full text-sm border border-slate-300 rounded-lg p-2.5 focus:border-amber-500 focus:outline-none"
              />
            </div>

            {/* URL Handle / Slug */}
            <div className="space-y-1">
              <label className="font-semibold text-xs text-slate-700 block">URL handle</label>
              <div className="flex items-center rounded-lg border border-slate-300 bg-slate-50 overflow-hidden focus-within:border-amber-500">
                <span className="text-xs text-slate-500 px-3 py-2 bg-slate-100 border-r border-slate-200 select-none">
                  blogs/{categorySlugPath}/
                </span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => {
                    setIsSlugManual(true);
                    setSlug(e.target.value.toLowerCase().replace(/[^\w-]/g, ''));
                  }}
                  placeholder="post-slug-url"
                  className="w-full text-sm bg-white p-2.5 focus:outline-none font-mono"
                />
              </div>
              <p className="text-[11px] text-slate-400 mt-1 font-mono">
                {previewUrl}
              </p>
            </div>
          </div>
        </div>

        {/* ── RIGHT COLUMN (Sidebar Settings) ── */}
        <div className="space-y-6">
          {/* 1. Visibility Card (Image 2) */}
          <div className="blog-card space-y-4">
            <div className="flex items-center justify-between">
              <label className="blog-card-title block">Visibility</label>
              <Calendar size={15} className="text-slate-400" />
            </div>

            <div className="space-y-2.5">
              <label className="flex items-center gap-3 cursor-pointer text-sm font-medium text-slate-800">
                <input
                  type="radio"
                  name="visibility"
                  value="visible"
                  checked={visibility === 'visible'}
                  onChange={() => setVisibility('visible')}
                  className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                />
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span>Visible</span>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer text-sm font-medium text-slate-800">
                <input
                  type="radio"
                  name="visibility"
                  value="hidden"
                  checked={visibility === 'hidden'}
                  onChange={() => setVisibility('hidden')}
                  className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                />
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-slate-400" />
                  <span>Hidden (Draft)</span>
                </div>
              </label>
            </div>

            <div className="space-y-2 border-t border-slate-100 pt-4">
              <label htmlFor="blog-publish-date" className="block text-xs font-semibold text-slate-700">Publish date and time</label>
              <input
                id="blog-publish-date"
                type="datetime-local"
                value={localDateTime(publishedAt)}
                onChange={e => setPublishedAt(e.target.value ? new Date(e.target.value).toISOString() : '')}
                className="w-full text-sm border border-slate-300 rounded-lg p-2.5"
              />
              <p className="text-xs text-slate-500">Your local time. Leave blank to use the time of publishing. This date does not schedule publication.</p>
            </div>

            {visibility === 'visible' && (
              <p className="text-xs text-emerald-700 bg-emerald-50 p-2.5 rounded-lg border border-emerald-200/60 leading-relaxed">
                This post is public and will appear on the blog index.
              </p>
            )}
          </div>

          {/* 2. Featured Image Card (Image 2) */}
          <div className="blog-card space-y-3">
            <div className="flex items-center justify-between">
              <label className="blog-card-title block">Featured Image</label>
              {featuredImage && (
                <button
                  type="button"
                  onClick={() => setFeaturedImage('')}
                  className="text-xs text-red-500 hover:underline"
                >
                  Remove
                </button>
              )}
            </div>

            {featuredImage ? (
              <div className="space-y-2">
                <div className="relative rounded-lg overflow-hidden border border-slate-200 group">
                  <img
                    src={getBlogImageUrl(featuredImage)}
                    alt="Featured"
                    className="w-full h-44 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition-opacity">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-xs"
                    >
                      Change File
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowMediaModal(true)}
                      className="text-xs"
                    >
                      From Uploads
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="blog-image-dropzone cursor-pointer"
                >
                  <div className="space-y-2">
                    <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mx-auto">
                      <ImageIcon size={20} />
                    </div>
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs font-semibold mb-1"
                      >
                        {uploadingImage ? 'Uploading...' : 'Upload Image'}
                      </Button>
                      <p className="text-[11px] text-slate-500">or drop an image to upload</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMediaModal(true)}
                    className="w-full text-xs font-medium flex items-center justify-center gap-1.5 py-1.5"
                  >
                    <Folder size={13} className="text-amber-600" />
                    <span>Choose from Uploads</span>
                  </Button>
                </div>
              </div>
            )}

            {/* Direct URL input fallback */}
            <div className="pt-1">
              <input
                type="text"
                value={featuredImage}
                onChange={(e) => setFeaturedImage(e.target.value)}
                placeholder="Or paste image URL (https://...)"
                className="w-full text-xs px-2.5 py-1.5 border border-slate-200 rounded text-slate-600 focus:outline-none focus:border-amber-500"
              />
            </div>

            {uploadingImage && (
              <p className="text-xs text-amber-600 font-medium">Uploading image...</p>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.jfif,.avif"
              onChange={handleImageFileChange}
              className="hidden"
            />
          </div>

          {/* 3. Organization Card (Images 2 & 3) */}
          <div className="blog-card space-y-4">
            <h4 className="blog-card-title text-base border-b pb-2">Organization</h4>

            {/* Author Selector */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-700">Author</label>
                <button
                  type="button"
                  onClick={() => setShowAddAuthorModal(true)}
                  className="text-xs text-amber-600 hover:underline flex items-center gap-0.5"
                >
                  <Plus size={12} /> New author
                </button>
              </div>

              <select
                value={authorId}
                onChange={(e) => {
                  setAuthorId(e.target.value);
                  const selected = authors.find(a => a.id === e.target.value);
                  setAuthorName(selected ? selected.name : '');
                }}
                className="w-full text-sm border border-slate-300 rounded-lg p-2.5 bg-white text-slate-800 focus:border-amber-500"
              >
                <option value="">Select an author...</option>
                {authors.map(a => (
                  <option key={a.id} value={a.id}>{a.name} {a.title ? `(${a.title})` : ''}</option>
                ))}
              </select>
            </div>

            {/* Blog / Category Selector */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-700">Blog / Category</label>
                <button
                  type="button"
                  onClick={() => setShowAddCategoryModal(true)}
                  className="text-xs text-amber-600 hover:underline flex items-center gap-0.5"
                >
                  <Plus size={12} /> New category
                </button>
              </div>

              <select
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  const selected = categories.find(c => c.id === e.target.value);
                  setCategoryName(selected ? selected.name : '');
                }}
                className="w-full text-sm border border-slate-300 rounded-lg p-2.5 bg-white text-slate-800 focus:border-amber-500"
              >
                <option value="">Select category...</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Tags Input (Chip tags) */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-700 block">Tags</label>

              <div className="border border-slate-300 rounded-lg p-2 bg-white flex flex-wrap gap-1.5 items-center min-h-[42px] focus-within:border-amber-500">
                {tags.map((t, idx) => (
                  <span key={idx} className="blog-tag-chip">
                    <span>{t}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(t)}
                      title="Remove tag"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onBlur={() => handleAddTag()}
                  placeholder={tags.length === 0 ? "Add tags (press Enter)..." : ""}
                  className="flex-1 min-w-[120px] text-xs border-none p-1 focus:outline-none"
                />
              </div>

              {/* Suggestions from existing tags */}
              {popularTags.length > 0 && (
                <div className="pt-1">
                  <p className="text-[11px] text-slate-400 mb-1">Suggested tags:</p>
                  <div className="flex flex-wrap gap-1">
                    {popularTags.slice(0, 8).map((pt, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleAddTag(pt.name)}
                        className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                          tags.includes(pt.name)
                            ? 'bg-amber-100 text-amber-800 border-amber-300 font-semibold'
                            : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        +{pt.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Theme template (Shopify Image 2 match) */}
            <div className="space-y-1.5 pt-2 border-t border-slate-100">
              <label className="text-xs font-semibold text-slate-700 block">Theme template</label>
              <select
                defaultValue="default"
                className="w-full text-xs border border-slate-300 rounded-lg p-2.5 bg-white text-slate-700"
              >
                <option value="default">Default blog post</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal: Quick Add Author ── */}
      {showAddAuthorModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h4 className="font-bold text-slate-900 text-base">Add New Author</h4>
              <button type="button" onClick={() => setShowAddAuthorModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Author Name *</label>
                <input
                  type="text"
                  required
                  placeholder="Enter author's full name"
                  value={newAuthorName}
                  onChange={(e) => setNewAuthorName(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Role / Title (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Senior SEO Strategist"
                  value={newAuthorTitle}
                  onChange={(e) => setNewAuthorTitle(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAddAuthorModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={creatingAuthor || !newAuthorName.trim()}
                onClick={handleCreateAuthor}
                className="admin-primary"
              >
                {creatingAuthor ? 'Adding...' : 'Add Author'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Quick Add Category ── */}
      {showAddCategoryModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h4 className="font-bold text-slate-900 text-base">Add New Category</h4>
              <button type="button" onClick={() => setShowAddCategoryModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Category Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Case Studies"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAddCategoryModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={creatingCategory || !newCategoryName.trim()}
                onClick={handleCreateCategory}
                className="admin-primary"
              >
                {creatingCategory ? 'Adding...' : 'Add Category'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Select from Uploads ── */}
      {showMediaModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[88vh] flex flex-col overflow-hidden border border-slate-200">
            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <ImageIcon size={18} className="text-amber-600" />
                <h4 className="font-bold text-slate-900 text-base">Select from Uploads</h4>
              </div>
              <button
                type="button"
                onClick={() => setShowMediaModal(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <AdminBlogUploads
                isModal={true}
                onSelectImage={(url) => {
                  setFeaturedImage(getBlogImageUrl(url));
                  setShowMediaModal(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
